// Phase 7 D3 — Track C / Track F. Settings copy.
//
// Covers all five settings sub-routes plus the shared shell nav labels and
// the Gmail OAuth return-handler toast strings (Q-5). One namespace per
// surface, structured to keep imports terse at call sites
// (`copy.settings.account.title`, `copy.settings.theme.timezoneLabel`).
//
// Phrasing rules per blueprint §13.4 / §13.8:
//   - calm and direct; no exclamation marks
//   - no emoji in structural strings
//   - no trailing punctuation in button labels
//   - sentence case
//
// Q-3b: account-deletion gate uses a typed phrase + password re-entry. The
// phrase itself ('delete my account') lives in shared/validation/auth.ts as
// `ACCOUNT_DELETION_PHRASE` — copy here references the user-facing helper
// text only.
//
// Q-9: theme persistence is server-backed. Copy reflects that toggling
// updates the account, not just the local browser.
//
// Q-12: notification toggles are per-alert-type, off by default, and surface
// the "future mobile push — not active in v2.0" disclaimer per AC-4.

export const settingsCopy = {
  // Shell + nav labels (sidebar on desktop, pill tabs on mobile <768px).
  shell: {
    title: 'Settings',
    nav: {
      account: 'Account',
      theme: 'Appearance',
      devices: 'Devices',
      integrations: 'Integrations',
      notifications: 'Notifications',
    },
    navAriaLabel: 'Settings sections',
  },

  account: {
    title: 'Account',
    description: 'Your sign-in identity and account-level controls.',
    emailLabel: 'Email',
    emailHelp: 'Email cannot be changed. Contact support to migrate to a new address.',
    changePasswordTitle: 'Change password',
    changePasswordDescription:
      'Changing your password signs out every session and revokes all refresh tokens.',
    currentPasswordLabel: 'Current password',
    newPasswordLabel: 'New password',
    confirmNewPasswordLabel: 'Confirm new password',
    newPasswordHelp: 'At least 12 characters.',
    changePasswordSubmit: 'Change password',
    changePasswordSuccessToastTitle: 'Password changed',
    changePasswordSuccessToastBody: 'Sign in again with the new password.',
    deleteAccountTitle: 'Delete account',
    deleteAccountDescription:
      'Permanently deletes your account, preferences, and module data. This cannot be undone.',
    deleteAccountTrigger: 'Delete account',
    deleteDialogTitle: 'Delete account',
    deleteDialogIntro:
      'This deletes every record associated with your account across every module. There is no undo.',
    deleteDialogPasswordLabel: 'Re-enter your password',
    deleteDialogPhraseLabel: 'Type "delete my account" to confirm',
    deleteDialogPhraseHelp:
      'Both the password and the phrase must match before the delete control activates.',
    deleteDialogConfirm: 'Delete account',
    deleteDialogCancel: 'Cancel',
    deleteSuccessToastTitle: 'Account deleted',
    deleteSuccessToastBody: 'Your data has been removed from this server.',
    genericErrorTitle: 'Something went wrong',
    genericErrorBody: 'Try again, or contact support if it keeps failing.',
    wrongPasswordError: 'That password is not correct.',
  },

  theme: {
    title: 'Appearance',
    description: 'Choose how DailyDeck looks. Saved to your account.',
    radioLegend: 'Theme',
    optionLight: 'Light',
    optionDark: 'Dark',
    optionSystem: 'Follow system',
    timezoneSectionTitle: 'Time zone',
    timezoneDescription:
      'Used to format dates and to schedule daily-deck delivery in your local time.',
    timezoneLabel: 'Time zone',
    timezonePlaceholder: 'Search time zones',
    timezoneEmpty: 'No matching time zone',
    timezoneNoticeFallback: 'Loaded a curated subset.',
    saveError: 'Could not save your changes.',
  },

  devices: {
    title: 'Devices',
    description: 'Active sessions and refresh tokens. Revoke any you do not recognise.',
    currentBadge: 'Current',
    columnDevice: 'Device',
    columnLastUsed: 'Last used',
    columnKind: 'Kind',
    kindSession: 'Session',
    kindRefresh: 'Refresh token',
    revokeButton: 'Revoke',
    revokeAllButton: 'Revoke all but current',
    revokeDialogTitle: 'Revoke this device',
    revokeDialogBody:
      'Sign-in on this device will end immediately. The next API call from it will fail with 401.',
    revokeDialogConfirm: 'Revoke',
    revokeDialogCancel: 'Cancel',
    revokeAllDialogTitle: 'Revoke every other device',
    revokeAllDialogBody:
      'Every device except the one you are currently using will be signed out.',
    revokeAllDialogConfirm: 'Revoke all',
    revokeSuccessToastTitle: 'Device revoked',
    revokeAllSuccessToastTitle: 'Other devices revoked',
    emptyTitle: 'No active devices',
    emptyBody: 'Your sessions and refresh tokens will appear here.',
    loadErrorTitle: 'Could not load devices',
    loadErrorBody: 'Try again in a moment.',
    deviceLabelFallback: 'Unnamed device',
  },

  integrations: {
    title: 'Integrations',
    description: 'Connect accounts to surface bills and important dates automatically.',
    gmailCardTitle: 'Gmail',
    gmailDisconnectedDescription:
      'Connect a Gmail mailbox so DailyDeck can detect upcoming bills from your inbox.',
    gmailConnectButton: 'Connect Gmail',
    gmailConnectedAs: 'Connected as',
    gmailDisconnectButton: 'Disconnect',
    gmailDisconnectDialogTitle: 'Disconnect Gmail',
    gmailDisconnectDialogBody:
      'DailyDeck will stop scanning your mailbox. Existing detected bills are preserved.',
    gmailDisconnectDialogConfirm: 'Disconnect',
    gmailDisconnectDialogCancel: 'Cancel',
    gmailDisconnectSuccessToast: 'Gmail disconnected',
    gmailLoadErrorTitle: 'Could not load Gmail status',
    gmailLoadErrorBody: 'Try again in a moment.',
    gmailUnconfiguredTitle: 'Gmail integration is not configured',
    gmailUnconfiguredBody:
      'The operator has not provisioned Gmail OAuth credentials yet. See docs/operations/gmail-setup.md for setup instructions.',
    // Q-5: connect=denied / connect=error → toast-and-stay-on-page.
    connectSuccessToastTitle: 'Gmail connected',
    connectSuccessToastBody: 'DailyDeck will start scanning your mailbox shortly.',
    connectDeniedToastTitle: 'Gmail consent declined',
    connectDeniedToastBody: 'No mailbox was connected.',
    connectErrorToastTitle: 'Gmail connection failed',
    connectErrorToastBody: 'Try connecting again, or check your network.',
    connectRetryButton: 'Retry',
    gmailScanButton: 'Scan Gmail',
    gmailScanningLabel: 'Scanning…',
    gmailScanSuccessToastTitle: 'Scan complete',
    gmailScanSuccessToastBody: (n: number): string =>
      n === 0
        ? 'No new bills detected.'
        : n === 1
          ? '1 new bill detected. Check Review.'
          : `${n} new bills detected. Check Review.`,
    gmailScanErrorToastTitle: 'Scan failed',
    gmailScanErrorToastBody: 'Try again in a moment.',
  },

  notifications: {
    title: 'Notifications',
    description:
      'Choose which alerts you want to receive. Off by default. For future mobile push — not active in v2.0.',
    sectionPayments: 'Payments',
    sectionDates: 'Dates',
    sectionUnknown: 'Unknown',
    unknownTooltip:
      'This alert type is no longer published by any active module. Remove it to clean up.',
    unknownRemove: 'Remove',
    saveError: 'Could not save your preferences.',
  },
} as const;
